import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmResetPin } from '../../api/productService';
import './ResetPin.css';
import ViewMessage from "../../components/ViewMessage";
import PinInputElement from "../../components/PinInputElement";

const ResetPin = () => {
  const { t } = useTranslation();
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'enter' | 'confirm'>('enter');
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError(t('pin.reset.invalid_link'));
      return;
    }
    setToken(tokenParam);
  }, [searchParams, t]);

  const handlePinChange = (idx: number, value: string, isConfirm: boolean = false) => {
    if (!/^[0-9]?$/.test(value)) return;

    if (isConfirm) {
      const newConfirmPin = [...confirmPin];
      newConfirmPin[idx] = value;
      setConfirmPin(newConfirmPin);
    } else {
      const newPin = [...pin];
      newPin[idx] = value;
      setPin(newPin);
    }

    setError('');
  };


  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.some(d => d === '')) {
      setError(t('pin.error_length'));
      return;
    }
    setStep('confirm');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (confirmPin.some(d => d === '')) {
      setError(t('pin.error_length'));
      return;
    }

    if (pin.join('') !== confirmPin.join('')) {
      setError(t('pin.error_mismatch'));
      return;
    }

    if (!token) {
      setError(t('pin.reset.invalid_link'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await confirmResetPin(token, pin.join(''));
      // Success - redirect to login
      navigate('/login', {
        state: {
          message: t('pin.reset.success_message')
        }
      });
    } catch (error: any) {
      setError(error.response?.data?.message || t('pin.error_backend'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="reset-pin-container">
        <div className="reset-pin-error">
          <h2>{t('pin.reset.invalid_link')}</h2>
          <p>The reset link is invalid or has expired.</p>
          <button
            className="reset-pin-back-button"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-pin-container">
      <ViewMessage
        title={'Oops!'}
        type={'error'}
        text={error}
        show={!!error}
        onClose={() => setError('')}
      />
      <div className="reset-pin-icon">
        <img src="/images/key-round.png" alt="Key icon" width={96} height={96} style={{ display: 'block' }} />
      </div>

      {step === 'enter' ? (
        <>
          <h2 className="reset-pin-title">{t('pin.reset.title')}</h2>
          <div className="reset-pin-instructions">
            <div>{t('pin.reset.instructions_line1')}</div>
            {/*<div>{t('pin.reset.instructions_line2')}</div>*/}
          </div>

          <form className="reset-pin-form" onSubmit={handleContinue} autoComplete="off">
            <PinInputElement
              stage="new"
              values={pin}
              onChange={(idx, val) => handlePinChange(idx, val)}
              borderType="highlight"
            />
            {error && <div className="reset-pin-error">{error}</div>}
            <div className='reset-pin-actions'>
              <button
                type="submit"
                className="reset-pin-continue"
                disabled={pin.some(d => d === '')}
              >
                {t('pin.reset.continue')}
              </button>
            </div>
          </form>
        </>
      ) : (
        <>
          <h2 className="reset-pin-title">{t('pin.reset.confirm_title')}</h2>
          <div className="reset-pin-instructions">
            <div>{t('pin.reset.confirm_instructions_line1')}</div>
            {/*<div>{t('pin.reset.confirm_instructions_line2')}</div>*/}
          </div>

          <form className="reset-pin-form" onSubmit={handleSubmit} autoComplete="off">
            <PinInputElement
              stage="confirm"
              values={confirmPin}
              onChange={(idx, val) => handlePinChange(idx, val, true)}
              autoFocusFirst
              borderType="default"
            />
            <div className='reset-pin-actions'>
              <button
                type="submit"
                className="reset-pin-submit"
                disabled={confirmPin.some(d => d === '') || isLoading}
              >
                {isLoading ? t('pin.reset.resetting') : t('pin.reset.submit')}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ResetPin;
